import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Megaphone, Film, Eye, DollarSign, TrendingUp, Clock, CheckCircle, ExternalLink, ArrowUpRight } from "lucide-react";
import { fmtCompact, fmtFull, fmtMoney, fmtStatus, timeAgo } from "@/lib/format";
import { PlatformIcon } from "@/components/campaign/PlatformIcon";

function StatCard({ title, value, icon: Icon, color, testId }) {
  return (
    <div
      data-testid={testId}
      className="group rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4 transition-transform duration-300 ease-out hover:-translate-y-1 hover:border-[#c8ff00]/25 hover:shadow-[0_18px_36px_-18px_rgba(200,255,0,0.45)]"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">{title}</div>
        {Icon && <div className={`rounded-lg p-1.5 ${color}`}><Icon size={15} /></div>}
      </div>
      <div data-testid={`${testId}-value`} className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-white">
        {value}
      </div>
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4">
      <Skeleton className="h-3 w-20 rounded bg-[#1a1a1a]" />
      <Skeleton className="mt-3 h-7 w-14 rounded bg-[#1a1a1a]" />
    </div>
  );
}

export function HomePage() {
  const nav = useNavigate();
  const { me } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/stats/overview"), api.get("/activity")]);
        setStats(s.data);
        setActivity(a.data);
      } catch (e) { setErr(apiErrorMessage(e)); }
    })();
  }, []);

  const topClip = (activity?.submissions || []).reduce((best, s) => {
    return (s.views || 0) > (best?.views || 0) ? s : best;
  }, null);

  return (
    <div data-testid="home-page" className="space-y-6">
      {/* Hero */}
      <div data-testid="home-hero" className="relative overflow-hidden rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-6">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 top-[-100px] h-[350px] w-[350px] rounded-full bg-[#c8ff00]/[0.04] blur-[100px] animate-[landing-pulse_8s_ease-in-out_infinite]" />
          <div className="absolute right-[-120px] top-[50px] h-[400px] w-[400px] rounded-full bg-[#c8ff00]/[0.03] blur-[120px] animate-[landing-float_12s_ease-in-out_infinite]" />
        </div>
        <div className="relative">
          <h1 data-testid="home-welcome" className="page-title text-xl sm:text-2xl">
            Welcome back, {me?.discord_username || me?.username || "Clipper"}
          </h1>
          <p data-testid="home-subtitle" className="page-subtitle">
            Track your performance, manage campaigns, and grow your earnings.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              data-testid="home-cta-campaigns"
              className="rounded-full bg-[#c8ff00] text-black font-bold hover:bg-[#b8ef00] transition-all active:scale-[0.97]"
              onClick={() => nav("/campaigns")}
            >
              Explore Campaigns
            </Button>
            <Button
              data-testid="home-cta-dashboard"
              className="rounded-full border border-[#1a1a1a] bg-transparent text-white font-medium hover:bg-[#111] transition-all"
              onClick={() => nav("/dashboard")}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {err && <div data-testid="home-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{err}</div>}

      {/* 2x2 Stats Grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {!stats ? (
          <><StatSkeleton /><StatSkeleton /><StatSkeleton /><StatSkeleton /></>
        ) : (
          <>
            <StatCard testId="stat-active-campaigns" title="Active Campaigns" value={stats?.active_campaigns ?? "0"} icon={Megaphone} color="bg-[#c8ff00]/10 text-[#c8ff00]" />
            <StatCard testId="stat-total-clips" title="Total Clips" value={stats?.total_clips_submitted ?? "0"} icon={Film} color="bg-cyan-500/10 text-cyan-400" />
            <StatCard testId="stat-total-views" title="Total Views" value={fmtCompact(stats?.total_views_generated || 0)} icon={Eye} color="bg-emerald-500/10 text-emerald-400" />
            <StatCard testId="stat-todays-earnings" title="Today's Earnings" value={fmtMoney(stats?.todays_earnings || 0)} icon={DollarSign} color="bg-amber-500/10 text-amber-400" />
          </>
        )}
      </div>

      {/* Top Performance */}
      {topClip && topClip.views > 0 && (
        <div data-testid="home-top-clip" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4 transition-all hover:border-[#c8ff00]/15">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-widest text-zinc-500">
              <TrendingUp size={14} className="text-[#c8ff00]" />
              Top Performing Clip
            </div>
            <button
              data-testid="home-top-clip-view"
              onClick={() => nav("/dashboard")}
              className="inline-flex items-center gap-1 rounded-full bg-[#c8ff00]/10 px-2.5 py-1 text-[11px] font-semibold text-[#c8ff00] transition-colors hover:bg-[#c8ff00]/20"
            >
              View In Dashboard <ArrowUpRight size={11} />
            </button>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular-nums text-white" title={fmtFull(topClip.views)}>{fmtCompact(topClip.views)}</span>
            <span className="text-sm text-zinc-500">views</span>
            <Badge className="ml-auto bg-[#1a1a1a] text-zinc-300 border-0 text-xs">{topClip.platform || "—"}</Badge>
          </div>
          {topClip.views_24h_delta != null && Number(topClip.views_24h_delta) > 0 && (
            <div data-testid="home-top-clip-delta" className="mt-2 text-[12px] font-semibold text-emerald-400">
              +{fmtFull(topClip.views_24h_delta)} Views <span className="text-zinc-500 font-normal">(Last 24 Hours)</span>
            </div>
          )}
        </div>
      )}

      {/* Activity Feed */}
      <div data-testid="activity-feed" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a1a1a]">
          <span data-testid="activity-feed-title" className="text-sm font-semibold text-white">Recent Activity</span>
          <Button
            data-testid="activity-clear"
            variant="ghost"
            size="sm"
            className="h-7 rounded-full text-xs text-zinc-600 hover:text-white hover:bg-white/5"
            onClick={async () => {
              try {
                await api.post("/activity/clear");
                setActivity({ submissions: [], transactions: [], notifications: [] });
                toast({ title: "Cleared" });
              } catch (e) {
                // ignore
              }
            }}
          >
            Clear all
          </Button>
        </div>
        <div className="p-4 space-y-2">
          {(activity?.submissions || []).slice(0, 5).map((s) => (
            <div key={s.id} data-testid={`activity-submission-${s.id}`} className="flex items-center gap-3 rounded-xl border border-[#1a1a1a] bg-black px-4 py-3 transition-colors hover:border-[#c8ff00]/15">
              <div className={`shrink-0 rounded-lg p-1.5 ${s.status === "accepted" ? "bg-emerald-500/10 text-emerald-400" : s.status === "flagged" || s.status === "rejected" ? "bg-rose-500/10 text-rose-400" : "bg-amber-500/10 text-amber-400"}`}>
                {s.status === "accepted" ? <CheckCircle size={14} /> : <Clock size={14} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                  {s.status === "accepted" ? "Clip Accepted" : s.status === "flagged" || s.status === "rejected" ? "Clip Rejected" : "Clip Submitted"}
                  {s.views > 0 && <span className="ml-1 text-xs text-emerald-400">+{fmtCompact(s.views)} views</span>}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
                  {s.platform ? <PlatformIcon platform={s.platform} size={11} /> : null}
                  <span className="font-medium text-zinc-400">{s.platform || "Unknown"}</span>
                  {s.campaign_name ? <span>· {s.campaign_name}</span> : null}
                  <span>· {timeAgo(s.created_at)}</span>
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.status === "accepted" ? "bg-emerald-500/15 text-emerald-400" : s.status === "flagged" || s.status === "rejected" ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`}>{fmtStatus(s.status)}</span>
              {s.video_url ? (
                <a
                  data-testid={`activity-view-clip-${s.id}`}
                  href={s.video_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#1a1a1a] bg-black px-2.5 py-1 text-[10.5px] font-semibold text-zinc-300 transition-colors hover:border-[#c8ff00]/40 hover:text-[#c8ff00]"
                >
                  <ExternalLink size={11} /> View Clip
                </a>
              ) : null}
            </div>
          ))}

          {(activity?.notifications || []).slice(0, 3).map((n) => (
            <div key={n.id} data-testid={`activity-notification-${n.id}`} className="rounded-xl border border-[#1a1a1a] bg-black px-4 py-3">
              <div className="text-sm font-medium text-white">{n.title}</div>
              <div className="mt-0.5 text-xs text-zinc-600">{n.message}</div>
            </div>
          ))}

          {!((activity?.submissions || []).length || (activity?.notifications || []).length) && (
            <div data-testid="activity-empty" className="rounded-xl border border-dashed border-[#1a1a1a] px-4 py-8 text-center text-sm text-zinc-600">
              Nothing yet — join a campaign and submit your first clip.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
